class ProductConfigurator extends (customElements.get('drawer-element') || HTMLElement) {
  constructor() {
    super();

    this.onOptionChangeListener = this.optionChange.bind(this);
    this.onOpenInfoPanel = this.openInfoPanel.bind(this);
    this.onCloseInfoPanel = this.closeInfoPanel.bind(this);
    this.onSubmitForm = this.submitForm.bind(this);
    this.onResetOptions = this.resetOptions.bind(this);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get focusElement() {
    return this.querySelector('.drawer__close') || super.focusElement;
  }

  connectedCallback() {
    // The registry fallback superclass is a plain HTMLElement with none of
    // DrawerElement's methods, so every super call checks first; a visitor
    // whose theme.js failed gets a quiet inert drawer, not a new error.
    // The same failure leaves theme.utils and theme.pubsub missing, so
    // init and event wiring are skipped outright in that state
    if (typeof super.connectedCallback === 'function') super.connectedCallback();
    if (!window.theme || !theme.utils) return;

    this.init();
    this.attachEvents();
  }

  disconnectedCallback() {
    if (typeof super.disconnectedCallback === 'function') super.disconnectedCallback();

    this.options.forEach(option => option.querySelector('input[type="checkbox"]')?.removeEventListener('change', this.onOptionChangeListener));

    this.infoPanelTriggers.forEach(button => button.removeEventListener('click', this.onOpenInfoPanel));

    this.infoPanelCloseButton.forEach(button => button.removeEventListener('click', this.onCloseInfoPanel));

    this.submitButton.removeEventListener('click', this.onSubmitForm);

    this.resetButton = this.querySelector('[data-reset]');
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onResetOptions);
    }

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange,
      this.variantChange.bind(this)
    );
  }

  resetOptions() {
    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    this.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.checked = false;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    });

    this.querySelectorAll('[data-section]').forEach(section => {
      const labelSelectedValue = section.querySelector('[data-selected-option]');
      const imageTag = section.querySelector('[data-media]');
      const infoBoxTrigger = section.querySelector('[data-target-tab]');
      const hasSwatchOption = section.querySelector('[data-swatch-option]');

      if (labelSelectedValue) labelSelectedValue.textContent = '';
      if (imageTag) imageTag.classList.add('hidden');

      if (infoBoxTrigger && hasSwatchOption) {
        infoBoxTrigger.classList.add('hidden');
      }
    });

    this.updatePrice();
  }

  attachEvents() {
    this.options.forEach(option => option.querySelector('input[type="checkbox"]')?.addEventListener('change', this.onOptionChangeListener));

    this.infoPanelTriggers.forEach(button => button.addEventListener('click', this.onOpenInfoPanel));

    this.infoPanelCloseButton.forEach(button => button.addEventListener('click', this.onCloseInfoPanel));

    this.submitButton.addEventListener('click', this.onSubmitForm);

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange,
      this.variantChange.bind(this)
    );
  }

  hide() {
    if (typeof super.hide === 'function') super.hide();

    this.closeInfoPanel();

    // A dismissed drawer takes any post-add panel with it; otherwise the
    // absolute overlay survives into the next open and covers a drawer
    // that can no longer be used
    const nudge = this.querySelector('.pcfg-nudge');
    if (nudge) {
      nudge.remove();
      this.serviceNudgeShown = false;
    }
  }

  init() {
    this.variantsOptions = this.querySelector('[data-variants-options]');
    this.sections = this.querySelectorAll('[data-section]');
    this.options = this.querySelectorAll('[data-option]');
    this.infoPanelTriggers = this.querySelectorAll('[data-target-tab]');
    this.infoPanel = this.querySelector('[data-info-panel]');
    this.infoPanelCloseButton = this.querySelectorAll('[data-close-panel]');
    this.submitButton = this.querySelector('[data-submit]');
    this.itemsObject = {};
    this.activeVariant = this.activeVariant ? this.activeVariant : JSON.parse(this.dataset.activeVariant);

    this.exclusionsMap = new Map();
    this.requiredMap = new Map();
    this.collectionMap = new Map();

    this.options.forEach(option => {
      option.collectionUse = new Map();

      const optionExclusionGroups = option.getAttribute('data-exclusion-groups');
      const optionRequiredWith = option.getAttribute('data-required-with');
      const optionCollections = option.getAttribute('data-collections');

      if (optionExclusionGroups) {
        const exclusionGroups = optionExclusionGroups.split(',');

        exclusionGroups.forEach((group) => {
          this.exclusionsMap.set(group, (this.exclusionsMap.get(group) ?? []).concat(option));
        });
      }

      if (optionCollections) {
        const collections = optionCollections.split(',');

        collections.forEach((collection) => {
          this.collectionMap.set(collection, (this.collectionMap.get(collection) ?? []).concat(option));
        });
      }

      if (optionRequiredWith) {
        const requiredWithGroups = optionRequiredWith.split(',');

        requiredWithGroups.forEach((group) => {
          this.requiredMap.set(group, (this.requiredMap.get(group) ?? []).concat(option));

          option.collectionUse.set(group, 0);
        });
      }
    })
  }

  variantChange({ data }) {
    this.activeVariant = data.variant;
    this.toggleOptionsVisibility();
    this.updateButtons();
  }

  updateButtons() {
    const hasVisibilityOption = Array.from(this.options).some(option => !option.classList.contains('hidden'));
    const buttonsWrapper = document.querySelector('[data-buy-buttons]');

    if (!buttonsWrapper) return;

    const configuratorButton = buttonsWrapper.querySelector('[data-configurator-button]');
    const defaultButtons = buttonsWrapper.querySelector('[data-default-buttons]');

    if (!configuratorButton || !defaultButtons) return;

    configuratorButton?.classList?.toggle('hidden', !hasVisibilityOption);
    defaultButtons?.classList?.toggle('hidden', hasVisibilityOption);
  }

  toggleOptionsVisibility() {
    this.options.forEach(option => {
      const checkbox = option.querySelector('input[type="checkbox"]');
      const price = parseInt(option.dataset.price);
      const parentVariantId = option.dataset.parentVariantId;

      option.classList.toggle('hidden', parentVariantId != this.activeVariant.id);

      if (checkbox && parentVariantId != this.activeVariant.id && price != 0) {
        checkbox.checked = false;

        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  optionChange(event) {
    const target = event.target;
    const option = target.closest('[data-option]');

    this.handleOptionExclusions(target, option);
    this.handleOptionRequired(target, option);
    this.handleOptionExcludes(target, option);

    if (target.closest('[data-swatch-option]')) {
      this.updateSwatchSection(target, option, event);
    }

    this.updatePrice();
  }

  updateSwatchSection(target, option, event) {
    const section = target.closest('[data-section]');

    if (!section) return;

    const imageTag = section.querySelector('[data-media]');
    const labelSelectedValue = section.querySelector('[data-selected-option]');
    const selectedValuePriceContainer = section.querySelector('[data-selected-option-price]');
    const infoBoxTrigger = section.querySelector('[data-target-tab]');

    const isTriggeredByExclusion = event.detail?.triggeredByExclusion || false;

    if (!isTriggeredByExclusion) {
      // Per-section single-select, scoped so a "one merged card" step can hold
      // BOTH the mutually-exclusive finishes (Premium Royal Exterior, Wood
      // Stain and Paint, …) AND an independent optional extra (Wood Preserver)
      // in the same swatch card. Finishes that share an exclusion group are
      // already made one-of across the whole configurator by
      // handleOptionExclusions, so we must not also sweep them here; and a
      // group-less extra must never clear them. Rule: a group-less swatch only
      // clears other group-less swatches; a grouped swatch leaves exclusion to
      // handleOptionExclusions. Products with no exclusion groups (every legacy
      // swatch step) are all group-less, so this behaves exactly as before.
      const targetGroups = (option?.getAttribute('data-exclusion-groups') || '').split(',').filter(Boolean);

      if (targetGroups.length === 0) {
        const activeSwatches = section.querySelectorAll('[data-swatch-option] input[type="checkbox"]:checked');

        activeSwatches?.forEach(checkbox => {
          if (target.checked !== true || checkbox === target || checkbox.checked !== true) return;

          const otherGroups = (checkbox.closest('[data-option]')?.getAttribute('data-exclusion-groups') || '').split(',').filter(Boolean);

          if (otherGroups.length === 0) {
            checkbox.checked = false;
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }
    }

    const activeSwatch = section.querySelector('[data-swatch-option] input[type="checkbox"]:checked');

    if (!activeSwatch) {
      const visibleSwatches = Array.from(section.querySelectorAll('[data-swatch-option]:not(.hidden)'));

      labelSelectedValue.textContent = '';
      imageTag.classList.add('hidden');
      infoBoxTrigger.classList.add('hidden');

      const prices = visibleSwatches.map((s) => parseInt(s.dataset.price)).filter((n) => !isNaN(n));

      if (prices.length) {
        // Cheapest visible option, labelled "from" when the card mixes
        // prices (a merged finishes card holds the stain family next to
        // the dearer Protek family); single-price sections read as before.
        const price = Math.min(...prices);
        const mixed = new Set(prices).size > 1;

        selectedValuePriceContainer.textContent = `${mixed ? 'from ' : ''}+${theme.Currency.formatMoney(price, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat)}`;
      } else {
        selectedValuePriceContainer.textContent = '';
      }

    } else {
      const parentActiveSwatch = activeSwatch.closest('[data-swatch-option]');
      const price = parseInt(parentActiveSwatch.dataset.price);

      labelSelectedValue.textContent = parentActiveSwatch.dataset.name;
      imageTag.src = parentActiveSwatch.dataset.image;
      imageTag.classList.remove('hidden');

      infoBoxTrigger.dataset.targetTab = parentActiveSwatch.dataset.infoTab;
      infoBoxTrigger.classList.remove('hidden');

      selectedValuePriceContainer.textContent = `+${theme.Currency.formatMoney(price, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat)}`;
    }
  }

  handleOptionExclusions(target, option) {
    const optionExclusionGroups = option.getAttribute('data-exclusion-groups');

    if (!optionExclusionGroups || !target.checked) return;

    const exclusionGroups = optionExclusionGroups.split(',');

    exclusionGroups.forEach(group => {
      const groups = this.exclusionsMap.get(group);

      groups.forEach((groupOption) => {
        if (groupOption !== option) {
          const groupCheckbox = groupOption.querySelector('input[type="checkbox"]');

          if (!groupCheckbox || !groupCheckbox.checked) return;

          groupCheckbox.checked = false;

          groupCheckbox.dispatchEvent(new CustomEvent("change", {
            bubbles: true,
            detail: {
              triggeredByExclusion: true
            }
          }));
        }
      });
    });
  }

  handleOptionRequired(target, option) {
    const optionCollections = option.getAttribute('data-collections');

    if (!optionCollections || this.requiredMap.size === 0) return;

    const collections = optionCollections.split(',');

    collections.forEach(collection => {
      const requiredCollectionGroup = this.requiredMap.get(collection);

      if (requiredCollectionGroup) {
        requiredCollectionGroup.forEach((groupOption) => {
          const delta = target.checked ? 1 : -1;
          groupOption.collectionUse.set(collection, groupOption.collectionUse.get(collection) + delta);

          const requiredCollection = Array.from(groupOption.collectionUse.values()).every(value => value);
          if (requiredCollection) {
            const groupCheckbox = groupOption.querySelector('input[type="checkbox"]');

            this.updateReadOnlyState(groupOption, 1);

            if (groupCheckbox && !groupCheckbox.checked) {
              groupCheckbox.checked = true;
              groupCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
          } else if (!requiredCollection && groupOption.readOnly) {
            this.updateReadOnlyState(groupOption, -1)
          }
        });
      }
    });
  }

  handleOptionExcludes(target, option) {
    const optionExcludesGroup = option.getAttribute('data-excludes');

    if (optionExcludesGroup) {
      const exclusionGroups = optionExcludesGroup.split(',');

      exclusionGroups.forEach(exclusionGroup => {
        const exclusionGroupOptions = this.collectionMap.get(exclusionGroup);

        if (exclusionGroupOptions) {
          exclusionGroupOptions.forEach(exclusionGroupOption => {
            const groupOptionCheckbox = exclusionGroupOption.querySelector('input[type="checkbox"]');

            this.updateReadOnlyState(exclusionGroupOption, target.checked ? 1 : -1);

            if (target.checked && groupOptionCheckbox && groupOptionCheckbox.checked) {
              groupOptionCheckbox.checked = false;

              groupOptionCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
          })
        }
      });
    }
  }

  updateReadOnlyState(option, count) {
    const checkbox = option.querySelector('input[type="checkbox"]');

    if (!checkbox) return;

    if (!('readOnlyCount' in checkbox)) {
      checkbox.readOnlyCount = 0;
    }

    checkbox.readOnlyCount += count;

    if (checkbox.readOnlyCount > 0 && !checkbox.readOnly) {
      checkbox.readOnly = true;
    } else if (checkbox.readOnlyCount < 1 && checkbox.readOnly) {
      checkbox.readOnly = false;
    }
  }

  openInfoPanel(event) {
    const target = event.target;
    const targetTabId = target.dataset.targetTab;

    event.preventDefault();

    // data-target-tab is empty until a swatch is chosen; querySelector('#')
    // (empty id) throws "'#' is not a valid selector", so bail before it.
    if (!targetTabId) return;

    const targetTab = this.querySelector(`#${targetTabId}`);
    const infoTabs = this.querySelectorAll('[data-info-panel] [data-info-tab]');

    if (!targetTab) return;

    this.infoPanel.setAttribute('open', true);

    infoTabs.forEach(infoTab => infoTab.classList.toggle('hidden', infoTab !== targetTab));
  }

  closeInfoPanel(event) {
    if (event) {
      event.preventDefault();
    }

    this.infoPanel.removeAttribute('open');
  }

  generateItems() {
    this.itemsObject = {
      items: []
    };

    this.itemsObject.items.push({
      id: this.activeVariant.id,
      quantity: 1,
      properties: {
        _key: this.dataset.parentKey
      }
    });

    const activeOptions = this.querySelectorAll('[data-option]:not(.hidden) input[type="checkbox"]:checked');

    activeOptions.forEach(activeOption => {
      const parent = activeOption.closest('[data-option]');

      this.itemsObject.items.push({
        id: parent.dataset.id,
        quantity: 1,
        properties: {
          _parentKey: parent.dataset.key
        }
      });
    });

    this.itemsObject.items.reverse();
  }

  updatePrice() {
    const activeOptions = this.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
    const totalPrice = Array.from(activeOptions).reduce(
      (accumulator, currentValue) => {
        const parent = currentValue.closest('[data-variant-option]') || currentValue.closest('[data-option]');
        if (!parent || parent.classList.contains('hidden')) return accumulator;

        const price = parseInt(parent.dataset.price);
        return accumulator + price;
      }, this.activeVariant.price);

    const moneyFormat = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;

    this.submitButton.querySelector('.btn-text').textContent = this.submitButton.dataset.textPattern.replace(
      '[money]',
      theme.Currency.formatMoney(totalPrice, moneyFormat)
    );

    // Finance messaging in the drawer tracks the running total (minor units)
    document.dispatchEvent(new CustomEvent('wizard:total', { detail: { total: totalPrice } }));
  }

  // Installation and painting carry the margin; when they exist for this
  // building and were left unticked, ask once before the add goes through.
  collectSkippedServices() {
    return Array.from(this.querySelectorAll('[data-checkbox-option]')).filter(option => {
      if (option.classList.contains('hidden') || option.hasAttribute('data-oos')) return false;
      const input = option.querySelector('input[type="checkbox"]');
      if (!input || input.checked) return false;
      if (!parseInt(option.dataset.price)) return false;
      const title = ((option.querySelector('.wz-opt__title') || option.querySelector('label') || option).textContent || '').trim();
      // Painting anywhere in the title; installation only near the front of
      // it, so a base with "Installation Included" mid-name stays out
      const installAt = title.search(/install/i);
      return /paint/i.test(title) || (installAt > -1 && installAt < 16);
    });
  }

  // Post-add, not a gate: the building is already in the cart when this
  // shows. A tick confirms the add, then the skipped services are offered
  // with one-tap adds of their own.
  showPostAddServices() {
    if (this.serviceNudgeShown) return false;
    const skipped = this.collectSkippedServices();
    if (!skipped.length) return false;
    this.serviceNudgeShown = true;

    const moneyFormat = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
    const host = this.querySelector('.drawer__inner') || this;
    const panel = document.createElement('div');
    panel.className = 'pcfg-nudge pcfg-nudge--added';
    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
    const rows = skipped.map((option) => {
      const title = escapeHtml(((option.querySelector('.wz-opt__title') || option.querySelector('label') || option).textContent || '').trim().replace(/\s+/g, ' '));
      const price = theme.Currency.formatMoney(parseInt(option.dataset.price), moneyFormat);
      return '<div class="pcfg-nudge__row" data-service-row>' +
        '<span class="pcfg-nudge__row-title">' + title + '</span>' +
        '<span class="pcfg-nudge__row-price">+ ' + price + '</span>' +
        '<button type="button" class="pcfg-nudge__row-add" data-service-add="' + option.dataset.id + '">Add</button>' +
        '</div>';
    }).join('');
    panel.innerHTML =
      '<div class="pcfg-nudge__panel" role="status">' +
      '<div class="pcfg-nudge__tick" aria-hidden="true"><svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="24" fill="none"/><path fill="none" d="M14 27l8 8 16-16"/></svg></div>' +
      '<p class="pcfg-nudge__title">Added to your cart</p>' +
      '<p class="pcfg-nudge__sub">Are you sure you don&rsquo;t want ' + (skipped.length > 1 ? 'these' : 'this') + ' with it?</p>' +
      rows +
      '<button type="button" class="button button--primary pcfg-nudge__add" data-view-cart>View your cart</button>' +
      '</div>';
    host.appendChild(panel);

    panel.querySelectorAll('[data-service-add]').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.disabled = true;
        btn.textContent = 'Adding';
        this.addServiceToCart(btn.getAttribute('data-service-add'))
          .then(() => {
            btn.textContent = 'Added';
            btn.closest('[data-service-row]').classList.add('is-added');
          })
          .catch(() => {
            btn.disabled = false;
            btn.textContent = 'Add';
          });
      });
    });
    panel.querySelector('[data-view-cart]').addEventListener('click', () => {
      panel.remove();
      this.hide();
      this.cartDrawer?.show();
    });
    return true;
  }

  addServiceToCart(variantId) {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
    const body = JSON.stringify({
      items: [{ id: parseInt(variantId), quantity: 1 }],
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });
    return fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then(response => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) throw new Error(parsedState.description);
        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-configurator', cart: cartJson });
      });
  }

  submitForm() {
    this.generateItems();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      ...this.itemsObject,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });

    this.handleErrorMessage();

    fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.handleErrorMessage(parsedState.description);

          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-configurator', cart: cartJson });

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        if (this.showPostAddServices()) return;

        this.hide();

        this.cartDrawer?.show();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
      });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessage = this.querySelector('[data-error-message]');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }
}

customElements.define('product-configurator', ProductConfigurator);
